// Напоминания: файл календаря (работает везде) и Web Push (где он реально доступен).
// Состояние push показывается честно, без обещаний обычной вкладке iPhone.
import { useEffect, useState } from 'react';
import { BellRinging, CalendarBlank } from '@phosphor-icons/react';
import { Button } from '@astryxdesign/core/Button';
import { Text } from '@astryxdesign/core/Text';
import { VStack } from '@astryxdesign/core/Stack';
import { useToast } from '@astryxdesign/core/Toast';
import { publicApi } from '@/lib/api/public';
import type { BookingView } from '@/lib/api/schemas';
import { buildIcs, downloadIcs } from '@/lib/ics';
import { pushState, subscribePush, type PushState } from '@/lib/push';
import { dateTimeLabel } from '@/lib/time';

const flagKey = (bookingId: string) => `studio:push:${bookingId}`;

function readFlag(id: string): boolean {
  try {
    return localStorage.getItem(flagKey(id)) === '1';
  } catch {
    return false;
  }
}

export function ReminderControls({ booking, token }: { booking: BookingView; token: string }) {
  const toast = useToast();
  const slug = booking.tenant.slug;
  const [state, setState] = useState<PushState | 'checking'>('checking');
  const [registered, setRegistered] = useState(() => readFlag(booking.id));
  const [reminderAt, setReminderAt] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void pushState(slug).then((s) => alive && setState(s));
    return () => {
      alive = false;
    };
  }, [slug]);

  const addToCalendar = () => {
    const url = `${location.origin}/s/${slug}/my#t=${token}`;
    downloadIcs(
      `zapis-${booking.starts_at.slice(0, 10)}.ics`,
      buildIcs({
        uid: `${booking.id}@${location.host}`,
        start: booking.starts_at,
        end: booking.work_ends_at,
        title: `${booking.service_name} — ${booking.tenant.name}`,
        description: `Автомобиль: ${booking.car}. Управление записью: ${url}`,
        location: booking.tenant.address ?? '',
        url,
      }),
    );
  };

  const enablePush = async () => {
    try {
      const keys = await subscribePush(slug);
      const res = await publicApi.registerPush(token, keys);
      try {
        localStorage.setItem(flagKey(booking.id), '1');
      } catch {
        /* ignore */
      }
      setRegistered(true);
      setState('subscribed');
      setReminderAt(res.reminder_due);
      toast({ body: 'Напоминание включено' });
    } catch (e) {
      setState(await pushState(slug));
      toast({ body: e instanceof Error ? e.message : 'Не удалось включить напоминание', type: 'error' });
    }
  };

  const pushNote: Partial<Record<PushState, string>> = {
    'ios-needs-install':
      'На iPhone напоминания приходят только из приложения на экране «Домой»: «Поделиться» → «На экран Домой», затем откройте приложение и включите напоминание здесь.',
    'not-configured': 'Студия не включила напоминания в браузере. Добавьте запись в календарь — он напомнит за сутки.',
    unsupported: 'Этот браузер не поддерживает уведомления. Добавьте запись в календарь — он напомнит за сутки.',
    denied: 'Уведомления для этого сайта запрещены в настройках браузера. Разрешите их или добавьте запись в календарь.',
  };

  const canPush = state === 'available' || (state === 'subscribed' && !registered);
  const active = booking.status === 'confirmed';

  return (
    <VStack gap={2}>
      <Button label="Добавить в календарь" icon={<CalendarBlank size={18} />} width="100%" onClick={addToCalendar} />
      {active && canPush ? (
        <Button
          label="Напомнить о визите"
          icon={<BellRinging size={18} />}
          width="100%"
          clickAction={enablePush}
        />
      ) : null}
      {active && registered && state === 'subscribed' ? (
        <Text color="secondary">
          {reminderAt
            ? `Напомним ${dateTimeLabel(reminderAt, booking.tenant.timezone)}.`
            : 'Напоминание включено на этом устройстве.'}
        </Text>
      ) : null}
      {active && state !== 'checking' && pushNote[state as PushState] ? (
        <Text type="supporting" color="secondary">
          {pushNote[state as PushState]}
        </Text>
      ) : null}
    </VStack>
  );
}
